<script setup lang="ts">
// One numeric column's filter as a trigger plus a popout: two number fields
// (typing and the native steppers), and a slider draggable from both ends over a
// magnitude-spaced track.
//
// The track is log-spaced because these columns span orders of magnitude —
// treatment.quantity runs median 1, p95 32, max 21,575, and area_treated runs
// median 1.9 m², p95 4,047 m², max 30.7M m². On an even track the whole useful
// range would be a sliver at the left edge of one huge outlier; on a log track
// "1 to 10" is as easy to drag as "100 to 1,000".
//
// Behind the track sit the distribution's bars, from the server, counted under
// the spec with THIS column's clause removed — so dragging moves the window
// rather than reshaping the histogram, and the number beside it can say how many
// rows the window leaves. Dragging does not refetch: the bars are already the
// answer for every window.
import { computed, nextTick, ref, watch } from "vue";

import { useStoreMapReview } from "@/store/map-review";
import { useSessionStore } from "@/store/session";
import type { MapReviewHistogramResponse, MapReviewTable } from "@/type/api";
import type { MapReviewColumn } from "./columns";

/** The slider's resolution. Fine enough that a decade is ~200 positions. */
const STEPS = 1000;

/** Square metres per display unit, for the area column. */
const AREA_M2_PER_UNIT: Record<string, number> = {
	acre: 4046.8564224,
	hectare: 10000,
};

interface Props {
	table: MapReviewTable;
	column: MapReviewColumn;
	/** The clause's current bounds, in the column's canonical unit. */
	min: number | null;
	max: number | null;
}
const props = defineProps<Props>();
const emit = defineEmits<{
	change: [range: { min: number | null; max: number | null } | null];
}>();

const store = useStoreMapReview();

const open = ref(false);
const rootEl = ref<HTMLElement | null>(null);
const panelEl = ref<HTMLElement | null>(null);
const panelStyle = ref<Record<string, string>>({});
const histogram = ref<MapReviewHistogramResponse | null>(null);

// The area column filters over canonical square metres but renders the
// district's own unit, so the panel shows what the cells show and converts back
// on the way out. Without the preference (or on a non-area column) the canonical
// number is shown as-is.
const areaUnit = ref("");
void useSessionStore()
	.get()
	.then((session) => {
		areaUnit.value = session.organization.unit_preference?.area ?? "";
	})
	.catch(() => {});

const factor = computed(() =>
	props.column.kind === "area" && AREA_M2_PER_UNIT[areaUnit.value]
		? AREA_M2_PER_UNIT[areaUnit.value]
		: 1,
);
const unitLabel = computed(() =>
	factor.value === 1
		? props.column.kind === "area"
			? "m²"
			: ""
		: areaUnit.value,
);

const toDisplay = (canonical: number): number => canonical / factor.value;
const toCanonical = (display: number): number => display * factor.value;

function formatValue(canonical: number): string {
	const display = toDisplay(canonical);
	const text = Number.isInteger(display)
		? display.toLocaleString()
		: display.toLocaleString(undefined, { maximumFractionDigits: 2 });
	return unitLabel.value ? `${text} ${unitLabel.value}` : text;
}

const hasRange = computed(() => props.min !== null || props.max !== null);
const summary = computed(() => {
	const { min, max } = props;
	if (min !== null && max !== null) {
		return min === max
			? formatValue(min)
			: `${formatValue(min)} – ${formatValue(max)}`;
	}
	if (min !== null) return `at least ${formatValue(min)}`;
	if (max !== null) return `at most ${formatValue(max)}`;
	return "";
});
const title = computed(() =>
	hasRange.value
		? `${props.column.label}: ${summary.value}`
		: `Filter ${props.column.label} by range`,
);

// The track's endpoints, in canonical units. They come from the column's own
// registry entry, so the slider is there the moment the panel opens; the
// histogram, when the server has one, counts into exactly this domain and only
// adds the bars. Making the slider wait for that response would have left the
// operator with two number fields and no slider at all.
const domainMin = computed(
	() => histogram.value?.domain_min ?? props.column.rangeDomain?.min ?? 0,
);
const domainMax = computed(
	() => histogram.value?.domain_max ?? props.column.rangeDomain?.max ?? 0,
);
const logScale = computed(
	() => histogram.value?.log_scale ?? props.column.rangeDomain?.log ?? true,
);
const ready = computed(
	() =>
		domainMax.value > domainMin.value &&
		!(logScale.value && domainMin.value <= 0),
);

const span = computed(() => {
	const lo = domainMin.value;
	const hi = domainMax.value;
	if (!ready.value) return 1;
	return logScale.value ? Math.log(hi / lo) : hi - lo;
});

/** Position (0..1) of a canonical value on the track. */
function positionOf(canonical: number): number {
	if (!ready.value) return 0;
	const clamped = Math.min(
		Math.max(canonical, domainMin.value),
		domainMax.value,
	);
	const offset = logScale.value
		? Math.log(clamped / domainMin.value)
		: clamped - domainMin.value;
	const ratio = offset / span.value;
	return logScale.value ? ratio : Math.min(Math.max(ratio, 0), 1);
}

/** The canonical value at a track position (0..1). */
function valueAt(position: number): number {
	const p = Math.min(Math.max(position, 0), 1);
	if (logScale.value) return domainMin.value * Math.exp(p * span.value);
	return domainMin.value + p * span.value;
}

const stepPos = computed(
	() => (position: number) => Math.round(position * STEPS),
);

const loPos = computed(() =>
	props.min === null ? 0 : stepPos.value(positionOf(props.min)),
);
const hiPos = computed(() =>
	props.max === null ? STEPS : stepPos.value(positionOf(props.max)),
);
const loValue = computed(() =>
	props.min === null ? domainMin.value : props.min,
);
const hiValue = computed(() =>
	props.max === null ? domainMax.value : props.max,
);

// The bars, positioned on the same track. A bar's width is its bucket's share of
// the track, which on a log scale is equal for every bucket; its height is its
// share of the tallest bucket, so the shape is honest rather than smoothed.
const bars = computed(() => {
	const response = histogram.value;
	if (!response || response.bins.length === 0) return [];
	const tallest = Math.max(...response.bins.map((b) => b.count), 1);
	return response.bins.map((bin) => {
		const left = positionOf(bin.from) * 100;
		const right = positionOf(bin.to) * 100;
		return {
			key: `${bin.from}`,
			left,
			width: Math.max(right - left, 0.4),
			height: (bin.count / tallest) * 100,
			count: bin.count,
			selected: bin.to > loValue.value && bin.from < hiValue.value,
		};
	});
});

const matching = computed(() => {
	const response = histogram.value;
	if (!response) return null;
	const inWindow = bars.value
		.filter((bar) => bar.selected)
		.reduce((sum, bar) => sum + bar.count, 0);
	return { inWindow, total: response.total, out: response.total - inWindow };
});

// Drag and type both land here. The field is the authority: a value at or below
// the domain's floor means "no lower bound", one at or above the ceiling means
// "no upper bound", so the ends of the track clear rather than pin to a number.
function applyRange(lo: number | null, hi: number | null): void {
	emit("change", lo === null && hi === null ? null : { min: lo, max: hi });
}

function onSlider(which: "min" | "max", event: Event): void {
	const position = Number((event.target as HTMLInputElement).value) / STEPS;
	const value = valueAt(position);
	if (which === "min") {
		applyRange(value, props.max);
	} else {
		applyRange(props.min, value);
	}
}

function onField(which: "min" | "max", event: Event): void {
	const raw = (event.target as HTMLInputElement).value.trim();
	const parsed = raw === "" ? null : Number(raw);
	const display = parsed === null || Number.isNaN(parsed) ? null : parsed;
	const canonical = display === null ? null : toCanonical(display);
	if (which === "min") {
		applyRange(canonical, props.max);
	} else {
		applyRange(props.min, canonical);
	}
}

function onFieldClear(which: "min" | "max"): void {
	if (which === "min") applyRange(null, props.max);
	else applyRange(props.min, null);
}

function clear(): void {
	emit("change", null);
}

// The bars are fetched once per spec, not per window: the endpoint counts them
// with this column's own clause removed, so the window never changes them.
async function loadBars(): Promise<void> {
	histogram.value = await store.histogram(props.table, props.column.key);
}
watch(() => [props.table, props.column.key], loadBars);

function positionPanel(): void {
	const trigger = rootEl.value?.querySelector("button");
	if (!trigger) return;
	const rect = trigger.getBoundingClientRect();
	const width = Math.max(rect.width, 300);
	const left = Math.min(
		Math.max(4, rect.left - width + rect.width),
		Math.max(4, window.innerWidth - width - 4),
	);
	panelStyle.value = {
		position: "fixed",
		left: `${left}px`,
		width: `${width}px`,
		top: `${rect.bottom + 2}px`,
	};
}

let onDocumentPointerDown: ((event: PointerEvent) => void) | null = null;
let onDocumentKeydown: ((event: KeyboardEvent) => void) | null = null;

async function openPanel(): Promise<void> {
	open.value = true;
	void loadBars();
	onDocumentPointerDown = (event: PointerEvent) => {
		const target = event.target as Node | null;
		if (!target) return;
		if (rootEl.value?.contains(target) || panelEl.value?.contains(target))
			return;
		closePanel();
	};
	onDocumentKeydown = (event: KeyboardEvent) => {
		if (event.key === "Escape") closePanel(true);
	};
	document.addEventListener("pointerdown", onDocumentPointerDown, true);
	document.addEventListener("keydown", onDocumentKeydown);
	// After the patch, not on requestAnimationFrame: a frame callback does not
	// run while the page is not being painted, and the panel would then render
	// unpositioned — in the body's flow, full width, which is what it did. The
	// date popout awaits nextTick for the same reason.
	await nextTick();
	positionPanel();
}

function closePanel(returnFocus = false): void {
	if (!open.value) return;
	open.value = false;
	if (onDocumentPointerDown)
		document.removeEventListener("pointerdown", onDocumentPointerDown, true);
	if (onDocumentKeydown)
		document.removeEventListener("keydown", onDocumentKeydown);
	onDocumentPointerDown = null;
	onDocumentKeydown = null;
	if (returnFocus) rootEl.value?.querySelector("button")?.focus();
}
</script>

<template>
	<div ref="rootEl" class="nr-root">
		<button
			type="button"
			class="nr-trigger"
			:class="{ 'nr-open': open, 'nr-empty': !hasRange }"
			:aria-expanded="open"
			:aria-label="title"
			:title="title"
			@click="open ? closePanel(true) : openPanel()"
		>
			<span class="nr-summary">{{ summary || "Any" }}</span>
			<i class="bi bi-sliders nr-icon" aria-hidden="true"></i>
		</button>
		<button
			type="button"
			class="nr-clear"
			:class="{ 'nr-clear-off': !hasRange }"
			:tabindex="hasRange ? 0 : -1"
			:aria-hidden="!hasRange"
			:aria-label="`Clear ${column.label} range filter`"
			:title="`Clear ${column.label} range filter`"
			@click="clear"
		>
			<i class="bi bi-x" aria-hidden="true"></i>
		</button>

		<Teleport to="body">
			<div
				v-if="open"
				ref="panelEl"
				class="nr-panel"
				:style="panelStyle"
				role="group"
				:aria-label="`Filter ${column.label} by range`"
			>
				<div v-if="ready" class="nr-chart">
					<div class="nr-bars">
						<span
							v-for="bar in bars"
							:key="bar.key"
							class="nr-bar"
							:class="{ 'nr-bar-out': !bar.selected }"
							:style="{
								left: `${bar.left}%`,
								width: `${bar.width}%`,
								height: `${bar.height}%`,
							}"
							:title="`${bar.count.toLocaleString()} rows`"
						></span>
					</div>
					<div class="nr-track">
						<div
							class="nr-track-fill"
							:style="{
								left: `${(loPos / STEPS) * 100}%`,
								right: `${100 - (hiPos / STEPS) * 100}%`,
							}"
						></div>
					</div>
					<input
						class="nr-range nr-range-lo"
						type="range"
						min="0"
						:max="STEPS"
						:value="loPos"
						:aria-label="`${column.label} minimum`"
						@input="onSlider('min', $event)"
					/>
					<input
						class="nr-range nr-range-hi"
						type="range"
						min="0"
						:max="STEPS"
						:value="hiPos"
						:aria-label="`${column.label} maximum`"
						@input="onSlider('max', $event)"
					/>
				</div>

				<div class="nr-fields">
					<label class="nr-field">
						<span class="nr-field-label">Min</span>
						<input
							class="nr-input"
							type="number"
							inputmode="decimal"
							:value="min === null ? '' : toDisplay(min)"
							:placeholder="ready ? String(toDisplay(domainMin)) : ''"
							:aria-label="`${column.label} minimum`"
							@change="onField('min', $event)"
						/>
						<button
							type="button"
							class="nr-field-clear"
							:class="{ 'nr-field-clear-off': min === null }"
							:tabindex="min === null ? -1 : 0"
							:aria-hidden="min === null"
							:aria-label="`Clear the ${column.label} minimum`"
							@click="onFieldClear('min')"
						>
							<i class="bi bi-x"></i>
						</button>
					</label>
					<label class="nr-field">
						<span class="nr-field-label">Max</span>
						<input
							class="nr-input"
							type="number"
							inputmode="decimal"
							:value="max === null ? '' : toDisplay(max)"
							:placeholder="ready ? String(toDisplay(domainMax)) : ''"
							:aria-label="`${column.label} maximum`"
							@change="onField('max', $event)"
						/>
						<button
							type="button"
							class="nr-field-clear"
							:class="{ 'nr-field-clear-off': max === null }"
							:tabindex="max === null ? -1 : 0"
							:aria-hidden="max === null"
							:aria-label="`Clear the ${column.label} maximum`"
							@click="onFieldClear('max')"
						>
							<i class="bi bi-x"></i>
						</button>
					</label>
				</div>

				<p v-if="matching" class="nr-note">
					Showing {{ matching.inWindow.toLocaleString() }} of
					{{ matching.total.toLocaleString() }} — filtering out
					{{ matching.out.toLocaleString() }}.
				</p>
				<p v-else-if="ready" class="nr-note nr-note-quiet">
					{{ store.total.toLocaleString() }} rows match. The distribution bars
					need the server's histogram, which this backend does not serve yet —
					the slider sets the range either way.
				</p>
			</div>
		</Teleport>
	</div>
</template>

<style scoped>
.nr-root {
	display: flex;
	align-items: center;
	gap: 2px;
	min-width: 0;
}

.nr-trigger {
	display: flex;
	align-items: center;
	gap: 3px;
	min-width: 0;
	height: 22px;
	padding: 0 4px;
	font-size: 0.75rem;
	color: #1b1f24;
	background: #fff;
	border: 1px solid #ced4da;
	border-radius: 2px;
	cursor: pointer;
}

.nr-summary {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.nr-empty .nr-summary {
	color: #667085;
}

.nr-icon {
	flex: 0 0 auto;
	color: #667085;
	font-size: 0.6875rem;
}

.nr-trigger:hover,
.nr-open {
	border-color: #94a3b8;
}

.nr-clear,
.nr-field-clear {
	border: 0;
	padding: 0 2px;
	background: transparent;
	color: #667085;
	line-height: 1;
	cursor: pointer;
}

/* The per-field clear keeps its slot whether or not there is a value to clear.
   It used to appear only once a value existed, which narrowed the input and
   moved the number field's stepper out from under a pointer already on it —
   clicking the arrows twice meant chasing the second click. The trigger's own
   clear holds its slot for the same reason. */
.nr-field-clear {
	flex: 0 0 auto;
	width: 16px;
}

.nr-clear {
	flex: 0 0 auto;
	width: 16px;
}

.nr-field-clear-off,
.nr-clear-off {
	visibility: hidden;
}

.nr-clear:hover,
.nr-field-clear:hover {
	color: #1b1f24;
}

.nr-panel {
	z-index: 40;
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 10px;
	background: #fff;
	border: 1px solid #ced4da;
	border-radius: 4px;
	box-shadow: 0 8px 24px -8px rgba(17, 24, 39, 0.24);
}

/* The bars grow out of the track: the chart is the space above it, so a tall
   bucket reads as "most rows are here" and the window's shading is what
   changes as the handles move. */
.nr-chart {
	position: relative;
	height: 74px;
}

.nr-bars {
	position: absolute;
	inset: 0 0 10px 0;
}

.nr-bar {
	position: absolute;
	bottom: 0;
	background: #c7d2de;
	border-radius: 1px 1px 0 0;
}

.nr-bar-out {
	background: #eef1f4;
}

.nr-track {
	position: absolute;
	left: 0;
	right: 0;
	bottom: 0;
	height: 4px;
	background: #eef1f4;
	border-radius: 2px;
}

.nr-track-fill {
	position: absolute;
	top: 0;
	bottom: 0;
	background: #94a3b8;
}

/* Two range inputs over one track. The inputs themselves are transparent to the
   pointer so each handle stays draggable where they overlap; the thumb takes the
   pointer back. */
.nr-range {
	position: absolute;
	left: 0;
	right: 0;
	bottom: -6px;
	width: 100%;
	height: 18px;
	margin: 0;
	background: none;
	pointer-events: none;
	appearance: none;
	-webkit-appearance: none;
}

.nr-range:focus-visible {
	outline: 2px solid #1a5fb4;
	outline-offset: 2px;
}

.nr-range::-webkit-slider-thumb {
	appearance: none;
	-webkit-appearance: none;
	pointer-events: auto;
	width: 14px;
	height: 14px;
	border-radius: 50%;
	background: #fff;
	border: 2px solid #475569;
	cursor: grab;
}

.nr-range::-moz-range-thumb {
	pointer-events: auto;
	width: 14px;
	height: 14px;
	border-radius: 50%;
	background: #fff;
	border: 2px solid #475569;
	cursor: grab;
}

.nr-range::-moz-range-track {
	background: none;
}

.nr-fields {
	display: flex;
	gap: 8px;
}

.nr-field {
	display: flex;
	align-items: center;
	gap: 4px;
	flex: 1 1 0;
	min-width: 0;
}

.nr-field-label {
	width: 26px;
	font-size: 0.6875rem;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #667085;
}

.nr-input {
	flex: 1 1 auto;
	min-width: 0;
	height: 24px;
	padding: 0 4px;
	font-size: 0.75rem;
	color: #1b1f24;
	border: 1px solid #ced4da;
	border-radius: 2px;
}

.nr-note {
	margin: 0;
	font-size: 0.75rem;
	color: #4a5259;
}

.nr-note-quiet {
	color: #667085;
}
</style>
